// @flow

import * as UserListModel from '../../models/user-list';
import * as LocationModel from '../../models/location';
import { TAG_TYPES, findTagTypeMeta } from '../location-tagging/tag-types';
import type { ReducerType as EntityTagsState } from '../../reducers/entityTags';

export type PreferenceTypeKey = 'location' | 'team_type' | 'tag';

export type PreferenceT = {
  id: number,
  userId: number,
  orgId: string,
  preferenceType: string,
  preferenceValue: string,
  createdAt?: string,
  updatedAt?: string,
};

// Explicit projection of the redux slices each registry callback may need.
// Consumers pull these via connect()'s mapStateToProps and pass them in —
// keeps useMemo dep arrays honest, avoids leaking the full AppState through
// the registry, and makes it explicit which slices a new preference type
// wants to read.
export type PreferenceDeps = {
  locations: LocationModel.t[],
  entityTags: EntityTagsState,
};

export type PillContent = {
  typeLabel: string,
  valueLabel: string,
  color: string,
  missing: boolean,
};

export type PreferenceOption = {
  id: string,
  label: string,
  color?: string,
  subTypeKey?: string,
  subTypeLabel?: string,
};

export type PreferenceTypeMeta = {
  key: PreferenceTypeKey,
  label: string,
  cardinality: 'single' | 'multi',
  color: ?string,
  validateValue: (value: string) => boolean,
  buildPillContent: (preference: PreferenceT, deps: PreferenceDeps) => PillContent,
  getOptions: (deps: PreferenceDeps) => PreferenceOption[],
  getUserValuesForType: (user: UserListModel.userT) => string[],
  // True when this type's resolution depends on a slice that is still
  // being hydrated. Drives the cell's per-row spinner so a still-loading
  // dependency doesn't flash a "not found" pill. Each type declares its
  // own pending-condition; the cell iterates the registry rather than
  // branching on key.
  isResolutionPending: (deps: PreferenceDeps) => boolean,
};

const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;
const isObjectId = (v: string): boolean =>
  typeof v === 'string' && OBJECT_ID_RE.test(v);

export const TEAM_TYPE_LABELS: { [string]: string } = {
  store_manager: 'Store Manager',
  region_manager: 'Region Manager',
  commercial: 'Commercial',
  customer_experience: 'Customer Experience / Insights',
  loss_prevention: 'Loss Prevention / Security',
  marketing: 'Marketing',
  merchandising: 'Merchandising',
  retail_operations: 'Retail Operations',
  systems_it: 'Systems / IT',
  hr_workforce_management: 'HR / Work force management',
};

const LOCATION_COLOR = '#5C7080';
const TEAM_TYPE_COLOR = '#9555A2';
const TAG_FALLBACK_COLOR = '#5C7080';

const filterUserPreferences = (
  user: UserListModel.userT,
  type: string,
): string[] => {
  const prefs =
    user && Array.isArray(user.preferences) ? user.preferences : [];
  return prefs
    .filter((p) => p && p.preferenceType === type)
    .map((p) => p.preferenceValue);
};

const locationMeta: PreferenceTypeMeta = {
  key: 'location',
  label: 'Location',
  cardinality: 'single',
  color: LOCATION_COLOR,
  validateValue: isObjectId,
  buildPillContent: (preference, { locations }) => {
    const found = (locations || []).find(
      (l) => l && l.id === preference.preferenceValue,
    );
    if (!found) {
      return {
        typeLabel: 'Location',
        valueLabel: 'not found',
        color: LOCATION_COLOR,
        missing: true,
      };
    }
    return {
      typeLabel: 'Location',
      valueLabel: found.name,
      color: LOCATION_COLOR,
      missing: false,
    };
  },
  getOptions: ({ locations }) =>
    (locations || [])
      .filter((l) => l && l.id && l.name)
      .map((l) => ({ id: l.id, label: l.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  getUserValuesForType: (user) => filterUserPreferences(user, 'location'),
  isResolutionPending: (_deps) => false,
};

const teamTypeMeta: PreferenceTypeMeta = {
  key: 'team_type',
  label: 'Team Type',
  cardinality: 'single',
  color: TEAM_TYPE_COLOR,
  validateValue: (value) =>
    typeof value === 'string' && TEAM_TYPE_LABELS.hasOwnProperty(value),
  buildPillContent: (preference, _deps) => {
    const label = TEAM_TYPE_LABELS[preference.preferenceValue];
    if (!label) {
      return {
        typeLabel: 'Team Type',
        valueLabel: 'not found',
        color: TEAM_TYPE_COLOR,
        missing: true,
      };
    }
    return {
      typeLabel: 'Team Type',
      valueLabel: label,
      color: TEAM_TYPE_COLOR,
      missing: false,
    };
  },
  getOptions: (_deps) =>
    Object.keys(TEAM_TYPE_LABELS).map((id) => ({
      id,
      label: TEAM_TYPE_LABELS[id],
    })),
  getUserValuesForType: (user) => filterUserPreferences(user, 'team_type'),
  isResolutionPending: (_deps) => false,
};

const tagMeta: PreferenceTypeMeta = {
  key: 'tag',
  label: 'Tag',
  cardinality: 'multi',
  color: null,
  validateValue: isObjectId,
  buildPillContent: (preference, { entityTags }) => {
    const byId = (entityTags && entityTags.byId) || {};
    const tag = byId[preference.preferenceValue];
    if (!tag) {
      return {
        typeLabel: 'Tag',
        valueLabel: 'not found',
        color: TAG_FALLBACK_COLOR,
        missing: true,
      };
    }
    const meta = findTagTypeMeta(TAG_TYPES, tag.tagType);
    return {
      typeLabel: meta.label,
      valueLabel: tag.tagValue,
      color: meta.color,
      missing: false,
    };
  },
  getOptions: ({ entityTags }) => {
    const byId = (entityTags && entityTags.byId) || {};
    const allIds = (entityTags && entityTags.allIds) || [];
    return allIds
      .map((id) => byId[id])
      .filter(Boolean)
      .map((tag) => {
        const meta = findTagTypeMeta(TAG_TYPES, tag.tagType);
        return {
          id: tag.id,
          label: tag.tagValue,
          color: meta.color,
          subTypeKey: tag.tagType,
          subTypeLabel: meta.label,
        };
      })
      .sort((a, b) => {
        const subA = a.subTypeKey || '';
        const subB = b.subTypeKey || '';
        if (subA !== subB) return subA.localeCompare(subB);
        return a.label.localeCompare(b.label);
      });
  },
  getUserValuesForType: (user) => filterUserPreferences(user, 'tag'),
  isResolutionPending: ({ entityTags }) =>
    !!(entityTags && entityTags.loading),
};

export const PREFERENCE_TYPES: PreferenceTypeMeta[] = [
  locationMeta,
  teamTypeMeta,
  tagMeta,
];

export const findPreferenceTypeMeta = (
  key: string,
): ?PreferenceTypeMeta =>
  PREFERENCE_TYPES.find((t) => t.key === key) || null;
